import {Inject, Injectable} from '@angular/core';
import {MerchantService, NotificationService, OdinFeatures, getActiveOrganisationId} from '@odin/odin-core';
import {UserAccessLevel} from '../models/user-access-level';
import {Router} from '@angular/router';

/**
 * @deprecated Use roleGuardService.hasPermission() instead.
*/
@Injectable({ providedIn: 'root' })
export class AccessGuardService {
  constructor(
    private router: Router,
    private merchantService: MerchantService,
    private notificationService: NotificationService,
    @Inject('features') private featureHelper: OdinFeatures,
  ) {}

  public boot(reason: string): void {
    this.router.navigate(['/dashboard']);
    this.notificationService.ErrorAlertDialog(reason, new Error(reason));
  }

  /** Sends the user to the dashboard with the standard access-denied dialog. */
  public bootNoAccess(featureName: string): void {
    this.router.navigate(['/dashboard']);
    this.notificationService.AccessDeniedDialog(featureName);
  }

  hasAccessLevel(accessLevel: UserAccessLevel): boolean {
    const profile = this.merchantService.getUserprofile();
    if (profile === null) return false;
    return profile.accessLevel == accessLevel || profile.accessLevel === UserAccessLevel.Admin;
  }

  isMinLevel(accessLevel: UserAccessLevel): boolean {
    if (this.hasAccessLevel(UserAccessLevel.Admin)) return true;
    const profile = this.merchantService.getUserprofile();
    if (profile === null) return false;
    return profile.accessLevel >= accessLevel;
  }

  buidHasFeature(feature: string): boolean {
    const profile = this.merchantService.getUserprofile();
    const buid = this.merchantService.getCurrentBuid();

    if (!profile || !buid || !buid.vorMerchantAccessOptions || buid.vorMerchantAccessOptions.length === 0) {
      return false;
    }

    const hasFeature = buid.vorMerchantAccessOptions.includes(feature);
    return hasFeature;
  }
  
  hasPartnerId(): boolean {
    const partnerId = this.merchantService.getPartnerIdFromProfile();
    return !!partnerId;
  }

  // Deprecated: Use hasNewGrant instead
  hasGrant(grantName: string): boolean {
    if (this.hasAccessLevel(UserAccessLevel.Admin)) return true;

    const profile = this.merchantService.getUserprofile();
    if (profile === null) return false;

    const matchingGrants = profile.grants.filter((g) => {
      if (g.action != grantName) return false;
      if (g.resource != this.merchantService.getMerchantRef()) return false;
      return true;
    });

    return matchingGrants.length > 0;
  }

  hasNewGrant(grantName: string): boolean {
    const profile = this.merchantService.getUserprofile();
    if (!profile || !Array.isArray(profile.grants)) return false;

    const { grants } = profile;
    const organisationId = getActiveOrganisationId(profile);
    const merchantRef = this.merchantService.getMerchantRef();

    // Merchant Admin (has org-level grant)
    if (grants.some((g) => g.resource === `OUID${organisationId}`)) {
      return true;
    }

    // Global Admin (Monek Admin)
    if (grants.some((g) => g.action === '*' && g.resource === '*')) {
      return true;
    }

    // Specific grant for the current merchant
    return grants.some((g) => g.action === grantName && g.resource === merchantRef);
  }
}